import { useMemo, useState } from 'react';
import { useLang } from 'hooks/lang';

import { loaders, LoadResults, UserAction } from 'entities';

import { Button, Column, Label, Modal, Row, Skeleton } from 'components/Blocks';
import { LightningIcon } from 'components/Icons';

import { useFocusedUser } from '../../focusedUser.hook';

import { getTimestampDisplay, isSameDay, subDays } from 'utils/date';

function renderJSON(data: UserAction['data']) {
  const lines = JSON.stringify(data, null, 2).split('\n');

  const withoutInitialCurly = lines.slice(1, lines.length - 1).join('\n');

  return withoutInitialCurly;
}

export function UserActionList() {
  const { t } = useLang();

  const { focusId } = useFocusedUser();

  const [data, setData] = useState<LoadResults['users.userActions']>(
    {} as LoadResults['users.userActions'],
  );

  const [openedAction, setAction] = useState<UserAction | null>(null);

  const { load } = loaders.users.userActions.use({
    blockLoad: !focusId,

    params: {
      userId: focusId,
    },

    onDataChange: setData,
  });

  const loadMore = async () => {
    if (!data?.paginationToken) return;

    const nextPage = await load({
      userId: focusId,
      paginationToken: data.paginationToken,
    });

    if (!nextPage) return;

    setData(({ items }) => ({
      items: [...items, ...nextPage.items],
      paginationToken: nextPage.paginationToken,
    }));
  };

  const groups = useMemo(() => {
    if (!data.items) return [];

    const today = new Date();
    const yesterday = subDays(today, 1);

    const [todayLogs, yesterdayLogs] = [today, yesterday].map((date) =>
      data.items.filter(({ timestamp }) => isSameDay(date, new Date(timestamp))),
    );

    const all = [...todayLogs, ...yesterdayLogs];

    return [
      {
        label: `${t('today')}`,
        logs: todayLogs,
      },
      {
        label: `${t('yesterday')}`,
        logs: yesterdayLogs,
      },
      {
        label: t('previous-logs'),
        logs: data.items.filter(({ id }) => !all.find((log) => log.id === id)),
      },
    ].filter(({ logs }) => logs.length);
  }, [data.items, t]);

  if (!data.items) return <Skeleton height={36} quantity={8} />;

  return (
    <>
      {openedAction && (
        <Modal
          isOpen
          close={() => setAction(null)}
          allowEscape
          preventOpenAutoFocus
          title={openedAction?.type}
          description={getTimestampDisplay(openedAction.timestamp)}
          width="min(90vw, 860px)"
          className="overflow-auto"
        >
          <pre className="whitespace-pre-wrap rounded-md border bg-muted p-4 font-mono leading-8">
            {renderJSON(openedAction.data)}
          </pre>
        </Modal>
      )}

      <Column className="px-2">
        {groups.map(({ label, logs }) => (
          <Column key={label} className="mt-2">
            <Label className="font-semibold">{label}</Label>

            {logs.map((action) => (
              <Row key={action.id}>
                <LightningIcon size={16} color="primary" />

                <Button
                  className="text-sm font-medium"
                  variant="link"
                  onClick={() => setAction(action)}
                >
                  {action.type}
                </Button>

                <span className="ml ml-auto text-sm">{getTimestampDisplay(action.timestamp)}</span>
              </Row>
            ))}
          </Column>
        ))}

        {!!data?.paginationToken && (
          <Button variant="outlined" onClick={loadMore}>
            Load more
          </Button>
        )}
      </Column>
    </>
  );
}
